import React, { useState } from 'react';

const INITIAL_PLAYBOOKS = [
  { id:1, name:'Club Discovery Call', type:'Sales call', uses:47, lastUsed:'Apr 3, 2026', status:'Active',
    steps:[
      { id:1, title:'Opening', script:'Hi [Name], this is [Rep] from TransferExpo. I\'m reaching out because your club has been expressing interest in finding new talent. Do you have 10 minutes to chat?' },
      { id:2, title:'Discovery questions', script:'• How many player requests do you typically post in a transfer window?\n• What positions are most in demand right now?\n• How are you currently scouting players globally?\n• What\'s your biggest challenge with finding players outside your region?' },
      { id:3, title:'Value proposition', script:'TransferExpo connects your club directly with certified scouts across [their region]. Instead of relying on agents, you get independent evaluations in [Standard at $10 or Premium at $25] — and you only pay when a scout delivers.' },
      { id:4, title:'Handle objections', script:'• "We already have scouts" → Our scouts are additive — they cover regions your network can\'t reach.\n• "Too expensive" → You only pay per evaluation. No subscription required to start.\n• "How do we know the quality?" → All scouts are certified. Premium evals include video analysis and written reports.' },
      { id:5, title:'Close', script:'Would you like to post your first player request today? I can walk you through it in 5 minutes. If you\'re not ready, I can send over a demo — which works better for you?' },
    ]
  },
  { id:2, name:'Scout Onboarding Call', type:'Onboarding', uses:31, lastUsed:'Apr 2, 2026', status:'Active',
    steps:[
      { id:1, title:'Welcome', script:'Hi [Name], welcome to TransferExpo! I\'m [Rep] and I\'ll be helping you get set up. This call should take about 15 minutes.' },
      { id:2, title:'Profile setup', script:'Let\'s make sure your profile is complete:\n✓ Certification level added?\n✓ Region set correctly?\n✓ Connected to your club?' },
      { id:3, title:'How evaluations work', script:'When a club posts a player request, you\'ll get a notification. You have 7 days to submit. Standard pays $5.25, Premium pays $13.13. Payment hits within 48 hours of acceptance.' },
      { id:4, title:'First eval walkthrough', script:'Let\'s find you your first open request right now. [Share screen] Here\'s a request from [Club] looking for a [Position] in your region...' },
    ]
  },
  { id:3, name:'Churn prevention', type:'Retention', uses:12, lastUsed:'Mar 28, 2026', status:'Active',
    steps:[
      { id:1, title:'Open', script:'Hi [Name], I noticed [Club] hasn\'t posted a new request in [X weeks]. I wanted to check in — is there anything we can do better?' },
      { id:2, title:'Listen & identify', script:'Listen for: budget constraints, lack of results, platform issues, found alternative. Don\'t interrupt — let them finish.' },
      { id:3, title:'Rescue offer', script:'Based on what you\'ve shared, I can offer: [discount / dedicated scout / feature walkthrough / exec introduction]. Would any of those help?' },
    ]
  },
];

export default function Playbooks({ toast }) {
  const [playbooks, setPlaybooks] = useState(INITIAL_PLAYBOOKS);
  const [selected, setSelected]   = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [creating, setCreating]   = useState(false);
  const [newName, setNewName]     = useState('');
  const [newType, setNewType]     = useState('Sales call');

  const playbook = playbooks.find(p=>p.id===selected);

  const create = () => {
    if (!newName.trim()) return;
    const p = { id:Date.now(), name:newName, type:newType, uses:0, lastUsed:'Never', status:'Active', steps:[{ id:1, title:'Opening', script:'Write your script here...' }] };
    setPlaybooks(prev=>[...prev,p]); setCreating(false); setNewName(''); toast?.('Playbook created');
  };

  if (playbook) {
    const step = playbook.steps[activeStep];
    return (
      <div>
        <button className="btn btn-ghost" style={{ marginBottom:16,fontSize:13 }} onClick={()=>{ setSelected(null); setActiveStep(0); }}>← Back to playbooks</button>
        <div style={{ display:'grid', gridTemplateColumns:'240px 1fr', gap:20 }}>
          {/* Steps nav */}
          <div className="card" style={{ alignSelf:'flex-start' }}>
            <div className="card-title" style={{ marginBottom:14,fontSize:13 }}>{playbook.name}</div>
            {playbook.steps.map((s,i)=>(
              <div key={s.id} onClick={()=>setActiveStep(i)}
                style={{ padding:'10px 12px',borderRadius:8,cursor:'pointer',marginBottom:4,background:activeStep===i?'rgba(167,244,50,0.1)':'transparent',border:`1px solid ${activeStep===i?'rgba(167,244,50,0.3)':'transparent'}` }}>
                <div style={{ fontSize:12,fontWeight:600,color:activeStep===i?'var(--accent)':'var(--text-primary)' }}>Step {i+1}</div>
                <div style={{ fontSize:11,color:'var(--text-dim)' }}>{s.title}</div>
              </div>
            ))}
            <div style={{ borderTop:'1px solid var(--border)',marginTop:12,paddingTop:12,display:'flex',gap:6 }}>
              <button className="btn btn-ghost" style={{ fontSize:11,flex:1 }} disabled={activeStep===0} onClick={()=>setActiveStep(v=>v-1)}>← Prev</button>
              <button className="btn btn-primary" style={{ fontSize:11,flex:1 }} disabled={activeStep===playbook.steps.length-1} onClick={()=>setActiveStep(v=>v+1)}>Next →</button>
            </div>
          </div>

          {/* Step detail */}
          <div className="card">
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20 }}>
              <div>
                <div style={{ fontSize:11,color:'var(--text-dim)',marginBottom:4 }}>Step {activeStep+1} of {playbook.steps.length}</div>
                <h2 style={{ fontFamily:'var(--font-display)',fontSize:20,fontWeight:500,color:'var(--text-primary)' }}>{step.title}</h2>
              </div>
              <span className="badge badge-green">Active playbook</span>
            </div>
            <div style={{ padding:'20px',background:'var(--bg-input)',borderRadius:10,fontSize:14,color:'var(--text-primary)',lineHeight:1.9,whiteSpace:'pre-wrap',borderLeft:'3px solid var(--accent)' }}>
              {step.script}
            </div>
            <div style={{ display:'flex',gap:8,marginTop:16 }}>
              <button className="btn btn-primary" onClick={()=>{ setPlaybooks(prev=>prev.map(p=>p.id===selected?{...p,uses:p.uses+1,lastUsed:new Date().toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}:p)); toast?.('Step marked complete'); if(activeStep<playbook.steps.length-1) setActiveStep(v=>v+1); else { setSelected(null); setActiveStep(0); toast?.('Playbook completed! 🎉'); } }}>
                {activeStep<playbook.steps.length-1?'Mark complete & next':'Finish playbook ✓'}
              </button>
              <button className="btn btn-ghost" onClick={()=>{ navigator.clipboard?.writeText(step.script); toast?.('Script copied to clipboard'); }}>Copy script</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total playbooks</div><div className="stat-value">{playbooks.length}</div><div className="stat-sub">All types</div></div>
        <div className="stat-card"><div className="stat-label">Total uses</div><div className="stat-value">{playbooks.reduce((s,p)=>s+p.uses,0)}</div><div className="stat-sub stat-up">All time</div></div>
        <div className="stat-card"><div className="stat-label">Active</div><div className="stat-value">{playbooks.filter(p=>p.status==='Active').length}</div><div className="stat-sub stat-up">In use</div></div>
        <div className="stat-card"><div className="stat-label">Avg steps</div><div className="stat-value">{Math.round(playbooks.reduce((s,p)=>s+p.steps.length,0)/playbooks.length)}</div><div className="stat-sub">Per playbook</div></div>
      </div>

      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16 }}>
        <h3 style={{ fontFamily:'var(--font-display)',fontSize:16,fontWeight:500,color:'var(--text-primary)' }}>Playbooks</h3>
        <button className="btn btn-primary" onClick={()=>setCreating(true)}>+ Create playbook</button>
      </div>

      {creating&&(
        <div className="card" style={{ marginBottom:16 }}>
          <div className="card-header"><span className="card-title">New playbook</span><button className="btn btn-ghost" style={{ fontSize:12 }} onClick={()=>setCreating(false)}>✕</button></div>
          <div style={{ display:'flex',gap:12,marginBottom:12 }}>
            <div style={{ flex:1 }}><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Name</label><input className="search-input" value={newName} onChange={e=>setNewName(e.target.value)} placeholder="e.g. Enterprise Discovery Call" /></div>
            <div><label style={{ fontSize:11,color:'var(--text-muted)',display:'block',marginBottom:5 }}>Type</label><select className="search-input" value={newType} onChange={e=>setNewType(e.target.value)}>{['Sales call','Onboarding','Retention','Demo','Support'].map(t=><option key={t}>{t}</option>)}</select></div>
          </div>
          <div style={{ display:'flex',gap:8 }}><button className="btn btn-primary" onClick={create}>Create</button><button className="btn btn-ghost" onClick={()=>setCreating(false)}>Cancel</button></div>
        </div>
      )}

      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:16 }}>
        {playbooks.map(p=>(
          <div key={p.id} className="card" style={{ cursor:'pointer' }} onClick={()=>setSelected(p.id)}>
            <div style={{ display:'flex',justifyContent:'space-between',marginBottom:10 }}>
              <span style={{ fontSize:11,padding:'3px 10px',borderRadius:99,background:'rgba(167,244,50,0.1)',color:'var(--accent)' }}>{p.type}</span>
              <span className="badge badge-green">{p.status}</span>
            </div>
            <div style={{ fontFamily:'var(--font-display)',fontSize:16,fontWeight:500,color:'var(--text-primary)',marginBottom:6 }}>{p.name}</div>
            <div style={{ fontSize:12,color:'var(--text-dim)',marginBottom:14 }}>{p.steps.length} steps · Used {p.uses} times · Last: {p.lastUsed}</div>
            <button className="btn btn-primary" style={{ fontSize:12,width:'100%' }}>Open playbook →</button>
          </div>
        ))}
      </div>
    </div>
  );
}
